import moment from 'moment';

import { EventType } from '../components/Chat/Chat.enum';
import { tChatEvent } from '../components/Chat/Chat.types';

import { isSummaryEvent } from '../components/Chat/ConversationSummary/ConversationSummary.functions.ts';

export function getCutOffTimeByHours(hours: number = 24): moment.Moment {
  return moment().subtract(hours, 'hours')
}

/**
 * Checks if a chat event is a recent, non-deleted message within the specified time window
 * @param event - The chat event to check
 * @param hours - Number of hours to look back (default: 24)
 * @returns True if the event is a recent, non-deleted message
 */
export function isRecentNonDeletedMessage(
  event: tChatEvent,
  hours: number = 24,
): boolean {
  // Check if event exists, is of type MESSAGE, and has a message property
  if (!event || event.type !== EventType.MESSAGE || !event.message) {
    return false;
  }

  // Check if the event is not deleted
  const notDeletedCondition =
    !event.deletedAt || event.deletedAt === '0001-01-01T00:00:00Z';
  if (!notDeletedCondition) {
    return false;
  }

  // Check if the event was created within the specified time window
  const eventTime = new Date(event.createdAt);
  const cutoffTime = new Date();
  cutoffTime.setTime(cutoffTime.getTime() - hours * 60 * 60 * 1000);

  return eventTime >= cutoffTime;
}

/**
 * Filters an array of chat events to return only recent, non-deleted messages
 * @param events - Array of chat events to filter
 * @param timeframeHours - Number of hours to look back (default: 24)
 * @returns Filtered array of recent, non-deleted messages
 */
export function filterRecentNonDeletedMessages(
  events: tChatEvent[],
  timeframeHours: number = 24,
): tChatEvent[] {
  const cutoffTime = new Date();
  cutoffTime.setTime(cutoffTime.getTime() - timeframeHours * 60 * 60 * 1000);

  return events.filter((event) => {
    const eventTime = new Date(event.createdAt);
    const timeCondition = eventTime >= cutoffTime;
    const typeCondition = event.type === EventType.MESSAGE;
    const messageCondition = !!event.message;
    const notDeletedCondition =
      !event.deletedAt || event.deletedAt === '0001-01-01T00:00:00Z';

    return (
      timeCondition && typeCondition && messageCondition && notDeletedCondition
    );
  });
}

export function findMostRecentSummary(
  leadsalesAI: tChatEvent[],
  hours: number = 24,
): tChatEvent[] {

  if (!leadsalesAI.length) {
    return [];
  }

  const cutoffTime = getCutOffTimeByHours(hours);

  const summaries = leadsalesAI
    .filter((event) => {
      if (!isSummaryEvent(event)) return false;
      const eventTime = moment(event.createdAt);
      
      return eventTime.isSameOrAfter(cutoffTime);
    })
    .sort(
      (a, b) =>
        moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf(),
    );

  return summaries;
}

export function filterMessagesAfterDate(
    events: tChatEvent[],
    cutoffTime: moment.Moment,
  ): tChatEvent[] {
    return events.filter((event) => {
      const wasDeleted = event.deletedAt && event.deletedAt !== '0001-01-01T00:00:00Z';
      if (event.type !== EventType.MESSAGE || wasDeleted) {
        return false;
      }
      return moment(event.createdAt).isSameOrAfter(cutoffTime);
    });
}
